/**
 * Email validation utilities
 */

/**
 * Validates if a string is a valid email address
 * @param {string} email - The email address to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export function isValidEmail(email) {
    if (!email || typeof email !== 'string') {
        return false;
    }
    
    // Trim whitespace
    const trimmedEmail = email.trim();
    
    // Basic email regex pattern
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    return emailRegex.test(trimmedEmail);
}

/**
 * Parses a comma-separated string of emails into an array
 * @param {string} emailString - Comma-separated email string
 * @returns {string[]} - Array of trimmed email addresses
 */
export function parseEmailList(emailString) {
    // Emails are stored as a comma-separated string, matching Pre-Security
    // Revamp. NG briefly persisted an array instead and those documents still
    // exist, so accept both — returning [] for an array (as this did) meant a
    // saved address never reappeared when the form was reopened.
    if (Array.isArray(emailString)) {
        return emailString
            .map((email) => String(email).trim())
            .filter((email) => email.length > 0);
    }
    if (!emailString || typeof emailString !== 'string') {
        return [];
    }
    
    return emailString
        .split(',')
        .map(email => email.trim())
        .filter(email => email.length > 0);
}

/**
 * Validates a list of emails and returns validation results
 * @param {string[]} emails - Array of email addresses to validate
 * @returns {Object} - Object with valid and invalid emails
 */
export function validateEmailList(emails) {
    const valid = [];
    const invalid = [];
    
    emails.forEach(email => {
        if (isValidEmail(email)) {
            valid.push(email);
        } else {
            invalid.push(email);
        }
    });
    
    return { valid, invalid };
}

/**
 * Converts an array of emails back to a comma-separated string
 * @param {string[]} emails - Array of email addresses
 * @returns {string} - Comma-separated email string
 */
export function formatEmailList(emails) {
    if (!Array.isArray(emails)) {
        return '';
    }
    
    return emails
        .map(email => email.trim())
        .filter(email => email.length > 0)
        .join(', ');
} 